/**
 * Profile Change Dropdown
 * Header component that allows switching between profiles
 * on desktop. Also displays onboarding status of users
 */
import { Stack, Tooltip, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import ProfileBubble from '@components/ProfileBubble';

import * as ROUTES from '@constants/routes';

import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';

import type { Profile } from '@models/session';

import { formatName } from '@utils/format';

import {
  Dropdown,
  DropdownDiv,
  DropdownOption,
  Header,
  NameDiv,
  OverflowHidden,
  SwitchAccountsDiv,
  TextCenter,
} from './ProfileChangeDropdown.style';

const messages = defineMessages({
  logOut: {
    id: 'profileChangeDropdown.logOut',
    defaultMessage: 'Log Out',
  },
  manageAccount: {
    id: 'profileChangeDropdown.manageAccount',
    defaultMessage: 'User Profile',
  },
  switchAccounts: {
    id: 'profileChangeDropdown.switchAccounts',
    defaultMessage: 'SWITCH ACCOUNTS',
  },
  viewingProfile: {
    defaultMessage: 'Viewing {name}',
    id: 'desktopHeader.viewingProfile',
  },
  orgSettings: {
    id: 'profileChangeDropdown.orgSettings',
    defaultMessage: 'Organization Settings',
  },
});

function ProfileChangeDropdown() {
  const { formatMessage } = useIntl();
  const { logout, state } = useSession();
  const { activeProfile, allProfiles, loggedInProfile, isOrgAdmin } = state;
  const { viewAsPatient, reset: resetCuration } = useCuration();

  const [display, setDisplay] = useState(false);

  const [first_name, last_name, user_id] = [
    activeProfile?.first_name,
    activeProfile?.last_name,
    activeProfile?.user_id,
  ];

  const changeUser = (user: Profile) => {
    viewAsPatient(user.user_id);
    setDisplay(false);
    if (!user.vitall_patient_id) {
      navigate(ROUTES.DASHBOARD);
    }
  };

  const handleLogOut = async () => {
    await resetCuration();
    await logout();
  };

  if (!user_id) {
    return null;
  }

  const handleClickManage = () => {
    navigate(ROUTES.MANAGE_ACCOUNT);
  };

  const handleOrgSettingClick = () => {
    navigate(ROUTES.ORG_MANAGEMENT);
  };

  const isViewing = Boolean(
    loggedInProfile?.user_id &&
    activeProfile?.user_id !== loggedInProfile.user_id
  );

  return (
    <Dropdown>
      <Header onClick={() => setDisplay(!display)} open={display}>
        <Tooltip
          arrow
          title={
            isViewing
              ? formatMessage(messages.viewingProfile, {
                  name: formatName(
                    activeProfile?.title,
                    activeProfile?.first_name,
                    activeProfile?.last_name
                  ),
                })
              : undefined
          }
        >
          <ProfileBubble
            fallback="circle-user"
            s3Key={activeProfile?.profile_picture}
            size={32}
            viewing={isViewing}
          />
        </Tooltip>
        <NameDiv>
          {formatName(undefined, first_name, last_name, undefined)}
        </NameDiv>
        <Icon
          clickable
          color="text.primary"
          name="chevron-down"
          rotation={display ? 180 : 0}
        />
      </Header>

      <DropdownDiv>
        <OverflowHidden className={display ? 'open' : ''}>
          {allProfiles.length > 1 && (
            <SwitchAccountsDiv>
              {formatMessage(messages.switchAccounts)}
            </SwitchAccountsDiv>
          )}
          <div
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            {allProfiles
              .filter((profile) => user_id !== profile.user_id)
              .map((user, index) => (
                <DropdownOption
                  key={`dropdown-option-${index}`}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={() => changeUser(user)}
                >
                  <ProfileBubble
                    fallback="circle-user"
                    s3Key={user.profile_picture}
                    size={32}
                  />
                  <NameDiv>
                    {formatName(user.title, user.first_name, user.last_name)}
                  </NameDiv>
                </DropdownOption>
              ))}
          </div>
          <SwitchAccountsDiv />
          <DropdownOption onClick={handleClickManage}>
            <Stack
              direction="row"
              columnGap={1}
              sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
            >
              <Icon color="text.primary" name="user" type="fas" />
              <Typography variant="body2" color="text.primary">
                {formatMessage(messages.manageAccount)}
              </Typography>
            </Stack>
          </DropdownOption>
          {isOrgAdmin && (
            <DropdownOption onClick={handleOrgSettingClick}>
              <Stack
                direction="row"
                columnGap={1}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Icon color="text.primary" name="users-gear" type="fas" />
                <Typography variant="body2" color="text.primary">
                  {formatMessage(messages.orgSettings)}
                </Typography>
              </Stack>
            </DropdownOption>
          )}
          <DropdownOption onClick={handleLogOut}>
            <TextCenter>
              <Typography color="text.primary">
                {formatMessage(messages.logOut)}
              </Typography>
            </TextCenter>
          </DropdownOption>
        </OverflowHidden>
      </DropdownDiv>
    </Dropdown>
  );
}

export default ProfileChangeDropdown;
